import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { post } from "../api/client";
import type { TeamDetail, YoutubeRequest } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { Bar, Check, ErrorLine, Seg, Sheet, useToast } from "../components/ui";
import { canPost, NO_POST, PostToYoutube, type PostChoice } from "../components/Youtube";
import { fmtBytes, fmtDate, titleFromFilename } from "../lib/format";
import { cancelUpload, loadSession, readDurationSeconds, sessionMatches, Uploader, type Progress, type UploadSession } from "../lib/upload";
import { useIsDesktop } from "../lib/device";
import { connectMessage, fieldProblem, useConnectResult, useYoutube, useYoutubeReturn } from "../lib/youtube";
import { planName } from "./Videos";

/** C17 wording, in this order. */
const REQS = ["Fixed camera position, both stumps visible", "Both creases visible for the whole video", "Up to 4 hours, 50 GB"];
type Pitch = "full" | "junior" | "unknown";

/**
 * 03 · Upload: A one page (file, pitch length, requirements, "Post to YouTube" where YouTube is on,
 * one button) and C uploading (one number, one line: keep this tab open). The YouTube choice goes with
 * the upload; an interrupted upload that resumes keeps the choice it started with, so the switch is
 * not shown for it.
 */
export default function Upload() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const desktop = useIsDesktop();
  const { me, plan, refresh } = useAuth();
  const managedTeams = (me?.teams ?? []).filter((t) => t.role === "manager");
  const canCreatePersonal = me?.can_create !== false;
  const [teamId, setTeamId] = useState<string>(() => {
    const qTeam = params.get("team");
    if (qTeam) return qTeam;
    const saved = loadSession()?.team_id;
    if (saved) return saved;
    return "";
  });
  const [newTeamOpen, setNewTeamOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamBusy, setNewTeamBusy] = useState(false);
  const [newTeamErr, setNewTeamErr] = useState<Error | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [durationS, setDurationS] = useState<number | null>(null);
  const [pitch, setPitch] = useState<Pitch>("full");
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [pending] = useState<UploadSession | null>(() => loadSession());
  const uploader = useRef<Uploader | null>(null);
  const picked = useRef<File | null>(null);
  const usage = me?.usage;
  const [toast, say] = useToast();

  useEffect(() => {
    if (!canCreatePersonal && !teamId && managedTeams.length > 0) {
      setTeamId(managedTeams[0]!.id);
    }
  }, [canCreatePersonal, teamId, managedTeams]);

  const selectedTeam = managedTeams.find((t) => t.id === teamId) ?? null;

  const ytOn = !!me?.features?.youtube;
  const yt = useYoutube(ytOn);
  const [choice, setChoice] = useState<PostChoice>(NO_POST);
  const [asked, setAsked] = useState(false);
  const [posted, setPosted] = useState<{ channel: string; privacy: string } | null>(null);
  const conn = yt.data?.connection ?? null;
  const allowed = canPost(yt.data, durationS);
  // Connected in the new window: the switch comes on (the user connected to post this video).
  useConnectResult((result) => {
    say(connectMessage(result));
    void yt.reload().then(() => {
      if (result === "connected") setChoice((c) => ({ ...c, on: true }));
    });
  }, () => void (conn && conn.state === "active" && !conn.long_uploads ? yt.refresh() : yt.reload()));
  useYoutubeReturn(say, () => void yt.reload());
  // A channel that cannot take this video: the switch goes off, and the user switches it on once verified.
  useEffect(() => {
    if (choice.on && yt.data && !allowed) setChoice((c) => ({ ...c, on: false }));
  }, [choice.on, allowed, yt.data]);

  useEffect(() => () => uploader.current?.cancelLocal(), []);

  const pick = (f: File | null) => {
    picked.current = f;
    setFile(f);
    setDurationS(null);
    setError(null);
    if (!f) return;
    setChoice((c) => (c.titleEdited ? c : { ...c, title: titleFromFilename(f.name) }));
    // Read now, not at Upload: the switch says before the upload when the channel cannot take this length.
    void readDurationSeconds(f).then((d) => {
      if (picked.current === f) setDurationS(d);
    });
  };
  const resumable = !!pending && !!file && sessionMatches(pending, file, teamId || null, null);
  const posting = ytOn && !resumable && choice.on && allowed;
  const kidsMissing = posting && asked && choice.kids === "";
  const change = (next: PostChoice) => {
    if (next.on !== choice.on) setAsked(false);
    setError(null);
    setChoice(next);
  };

  const createInlineTeam = async (e: FormEvent) => {
    e.preventDefault();
    const name = newTeamName.trim();
    if (!name || newTeamBusy) return;
    setNewTeamBusy(true);
    setNewTeamErr(null);
    try {
      const created = await post<TeamDetail>("/v1/teams", { name });
      await refresh();
      setTeamId(created.team.id);
      setNewTeamName("");
      setNewTeamOpen(false);
      say(`Team ${created.team.name} created.`);
    } catch (e) {
      setNewTeamErr(e as Error);
    } finally {
      setNewTeamBusy(false);
    }
  };

  const start = async () => {
    if (!file) return;
    let youtube: YoutubeRequest | undefined;
    if (posting) {
      if (choice.kids === "") {
        setAsked(true);
        // the question may be out of sight (on a phone, under the bar): bring it up
        document.getElementById("yt-kids")?.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }
      const problem = fieldProblem(choice.title, choice.showDesc ? choice.description : "");
      if (problem) return setError(problem);
      youtube = { title: choice.title.trim(), description: choice.showDesc ? choice.description.trim() : "", privacy_status: choice.privacy, made_for_kids: choice.kids === "yes" };
    }
    setError(null);
    const duration = durationS ?? (await readDurationSeconds(file)) ?? 60; // undecodable in the browser: a nominal hint; ingest measures the real one
    const up = new Uploader(file, setProgress);
    uploader.current = up;
    setPosted(youtube && conn ? { channel: conn.channel_title, privacy: youtube.privacy_status } : null);
    setProgress({ sent: 0, total: file.size, parts_done: 0, parts_expected: 0 });
    try {
      const { result } = await up.run({
        pitch_length_choice: pitch,
        duration_hint_s: Math.max(1, Math.round(duration)),
        title: titleFromFilename(file.name),
        team_id: teamId || null,
        ...(youtube ? { youtube } : {}),
      });
      nav(`/videos/${result.video_id}`, { replace: true });
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setError((e as Error).message);
      setProgress(null);
      if (youtube) void yt.refresh();
    }
  };
  const cancel = async () => {
    uploader.current?.cancelLocal();
    const s = loadSession();
    if (s) await cancelUpload(s).catch(() => undefined);
    setProgress(null);
    nav("/", { replace: true });
  };

  if (progress && file) {
    const pct = progress.total ? Math.floor((progress.sent / progress.total) * 100) : 0;
    return (
      <Shell>
        <div className="p center" style={{ paddingTop: "clamp(120px, 26vh, 240px)", maxWidth: 560 }} data-testid="uploading">
          <div className="huge" data-testid="upload-pct">{pct}%</div>
          <span className="sr-only" role="status" aria-atomic="true">Upload {Math.floor(pct / 10) * 10}% complete.</span>
          <p className="sub mt10">{titleFromFilename(file.name)}{desktop ? " · " : <br />}{fmtBytes(progress.sent)} of {fmtBytes(progress.total)}</p>
          <div className="mt32"><Bar value={pct / 100} label="Video upload" /></div>
          <p className="tiny mt24">Keep this tab open until the upload finishes. We email you when the clips are ready.</p>
          {posted && <p className="tiny mt10" data-testid="uploading-yt">Once it is checked, we post it to {posted.channel} on YouTube, {posted.privacy}, and email you the link.</p>}
          <div className="mt32"><button className="btn ghost wide" onClick={cancel}>Cancel upload</button></div>
        </div>
      </Shell>
    );
  }

  if (!canCreatePersonal && managedTeams.length === 0) {
    return (
      <Shell title="Upload" back="/" right={<span />}>
        <div className="p" style={{ paddingTop: 32, maxWidth: 520 }} data-testid="upload-blocked">
          <h1>Upload is for team managers</h1>
          <p className="sub mt10">
            Your account is a team member account, so you can watch and download clips assigned to your @handle free. Ask your team manager to make you a manager if you need to upload matches.
          </p>
          <div className="mt24">
            <Link to="/teams" className="btn ghost wide">View your teams</Link>
          </div>
        </div>
      </Shell>
    );
  }

  const drop = (
    <div className={`drop${over ? " over" : ""}`} style={{ height: desktop ? 160 : 130 }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) pick(f); }}>
      <div className="med" style={desktop ? { fontSize: 16 } : undefined}>{file ? file.name : desktop ? "Drop a video here" : "Choose a video"}</div>
      <div className="tiny">{file ? `${fmtBytes(file.size)} · ${resumable ? "an interrupted upload of this file will resume" : "ready to upload"}` : `${desktop ? "or choose a file · " : ""}MP4, MOV · up to 4 h / 50 GB`}</div>
      <input type="file" aria-label="Choose a match video" aria-describedby="upload-file-help" accept="video/mp4,video/quicktime,.mp4,.mov" onChange={(e) => pick(e.target.files?.[0] ?? null)} data-testid="file-input" />
      <span className="sr-only" id="upload-file-help">MP4 or MOV video, up to four hours and fifty gigabytes. {file ? `Selected file: ${file.name}.` : "No file selected."}</span>
    </div>
  );
  const footLine = selectedTeam?.usage
    ? `${selectedTeam.name} (${planName(selectedTeam.plan_code)}) · ${selectedTeam.usage.videos_used + 1} of ${selectedTeam.usage.videos_per_month} this month · resets ${fmtDate(selectedTeam.usage.period_end)}`
    : usage
      ? `${usage.videos_used + 1} of ${usage.videos_per_month} ${desktop ? "videos " : ""}this month · clips kept ${usage.retention_days} days on ${planName(plan)}`
      : "";
  const requirements = <div className="mt6">{REQS.map((r) => <Check key={r}>{r}</Check>)}</div>;
  const cta = posting
    ? "Upload and post"
    : selectedTeam
      ? `Upload to ${selectedTeam.name}`
      : "Upload";
  const kidsAlert = kidsMissing ? <p role="alert" className="err" data-testid="yt-kids-alert">Answer "Made for kids?" to post it to YouTube.</p> : null;

  return (
    <Shell title="Upload" back="/" right={<span />}
      foot={!desktop && (
        <>
          {kidsAlert && <div style={{ marginBottom: 10 }}>{kidsAlert}</div>}
          <button className="btn" onClick={start} disabled={!file} data-testid="upload-btn">{cta}</button>
          <p className="tiny center mt10">{footLine}</p>
        </>
      )}>
      <div className="p" style={{ paddingTop: desktop ? 48 : 8, paddingBottom: 24, maxWidth: desktop ? 720 : undefined }}>
        {desktop && <h1 style={{ fontSize: 32 }}>Upload a match</h1>}
        {pending && !file && <div className="notice mt16">An upload of <b>{pending.filename}</b> was interrupted. Choose the same file to resume it.</div>}
        <div className={desktop ? "mt32" : ""}>{drop}</div>
        <div className="row mt24" style={{ maxWidth: desktop ? 420 : undefined }}>
          <h3 id="upload-team-label" style={{ margin: 0 }}>Team</h3>
          {canCreatePersonal && (
            <button type="button" className="tiny u" onClick={() => { setNewTeamErr(null); setNewTeamOpen(true); }} data-testid="upload-new-team-btn">
              + New team
            </button>
          )}
        </div>
        <div className="mt8" style={{ maxWidth: desktop ? 420 : undefined }}>
          <select
            className="field"
            aria-labelledby="upload-team-label"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            data-testid="upload-team-select"
          >
            {canCreatePersonal && <option value="">Personal (only me)</option>}
            {managedTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (Manager)
              </option>
            ))}
          </select>
        </div>
        <p className="tiny mt6">
          {selectedTeam
            ? `Managers of ${selectedTeam.name} can assign player clips by @handle.`
            : "You can link this match to a team now or later when assigning @handles."}
        </p>
        <h3 id="upload-pitch-label" className={desktop ? "mt32" : "mt24"}>Pitch length</h3>
        <div className={desktop ? "mt16" : "mt10"} style={desktop ? { maxWidth: 360 } : undefined}>
          <Seg labelledBy="upload-pitch-label" value={pitch} onChange={setPitch} options={[{ value: "full", label: "Full · 66 ft" }, { value: "junior", label: "Junior" }, { value: "unknown", label: "Not sure" }]} />
        </div>
        <p className="tiny mt6">Needed for speed and length. "Not sure" means we check it for you.</p>
        <h3 className={desktop ? "mt48" : "mt32"}>Before you upload</h3>
        {requirements}
        {ytOn && !resumable && <PostToYoutube status={yt.data} durationS={durationS} choice={choice} onChange={change} missing={kidsMissing} desktop={desktop} />}
        {error ? <p className="err mt16" data-testid="upload-error">{error}</p> : null}
        {desktop && kidsAlert && <div className="mt32" style={{ textAlign: "right" }}>{kidsAlert}</div>}
        {desktop && (
          <div className={`row between ${kidsAlert ? "mt16" : "mt48"}`}>
            <span className="tiny">{footLine}</span>
            <button className="btn wide" onClick={start} disabled={!file} data-testid="upload-btn">{cta}</button>
          </div>
        )}
      </div>
      {newTeamOpen && (
        <Sheet onClose={() => !newTeamBusy && setNewTeamOpen(false)}>
          <form onSubmit={createInlineTeam} data-testid="upload-create-team-sheet">
            <h2>Create a team</h2>
            <label className="tiny mt16" style={{ display: "block" }}>Team name</label>
            <input
              className="field mt6"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="Vikings XI"
              required
              data-testid="upload-new-team-name"
            />
            <ErrorLine error={newTeamErr} />
            <div className="mt20">
              <button type="submit" className="btn dark full" disabled={newTeamBusy || !newTeamName.trim()} data-testid="upload-create-team-submit">
                {newTeamBusy ? "Creating…" : "Create team"}
              </button>
            </div>
            <div className="mt10">
              <button type="button" className="btn ghost full" onClick={() => setNewTeamOpen(false)} disabled={newTeamBusy}>
                Cancel
              </button>
            </div>
          </form>
        </Sheet>
      )}
      {toast}
    </Shell>
  );
}
